import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, LogOut } from 'lucide-react';
import { getLearnerNavItems } from './navigationHelpers';
import { dataLayer } from '../../data/dataLayer';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useBodyScrollLock } from '../../app/hooks/useBodyScrollLock';
import { APP_NAME } from '../../config/constants';
import { useDjaySelection, DjayAvatar, useVariantUnlocks, TOTAL_VARIANTS } from '../../mascot';
import { ACHIEVEMENTS } from '../progress/AchievementsSection';
import { useAchievementCount } from '../shared/useAchievementCount';
interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  currentView: string;
  onNavigate: (view: string) => void;
  onLogout?: () => void;
}
export function MobileNav({
  isOpen,
  onClose,
  currentView,
  onNavigate,
  onLogout
}: MobileNavProps) {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const {
    accounts,
    state,
    currentLearnerDisplayName,
    activeAccount,
    viewingLearnerId
  } = useAppStore();
  // Derive values from store
  const stats = state.stats;
  const displayName = currentLearnerDisplayName;
  const activeAccountRole = viewingLearnerId ? 'learner' : activeAccount?.role || 'learner';
  const isLearner = activeAccountRole === 'learner';
  // Load custom avatar for learners
  useEffect(() => {
    if (isLearner && isOpen) {
      const loadAvatar = async () => {
        const prefs = await dataLayer.getLearnerPreferences();
        if (prefs?.avatarUrl) {
          setAvatarUrl(prefs.avatarUrl);
        }
      };
      loadAvatar();
    }
  }, [isLearner, isOpen, accounts?.activeAccountId]);
  const [hasDiscoveredLongPress, setHasDiscoveredLongPress] = useState(false);
  useEffect(() => {
    if (isLearner && activeAccount?.id && isOpen) {
      dataLayer.getDjayLongPressDiscovered(activeAccount.id).then(setHasDiscoveredLongPress).catch(() => {});
    }
  }, [isLearner, activeAccount?.id, isOpen]);
  const {
    selectedVariant
  } = useDjaySelection();
  // Use shared hook for variant unlock counting (single source of truth)
  const {
    unlockedCount
  } = useVariantUnlocks(hasDiscoveredLongPress);
  // Use shared achievement count hook (single source of truth)
  const unlockedAchievementsCount = useAchievementCount();
  const [hasActiveAssignments, setHasActiveAssignments] = useState(false);
  useEffect(() => {
    if (isLearner && activeAccount?.id && isOpen) {
      const checkAssignments = async () => {
        const [assessments, practice] = await Promise.all([dataLayer.getTeacherAssignments(), dataLayer.getPracticeAssignments()]);
        const now = new Date();
        const hasValidAssessments = Object.values(assessments).some(a => {
          if (a.deadline && new Date(a.deadline) < now) return false;
          if (a.completedByStudentIds?.includes(activeAccount.id)) return false;
          return a.wholeClass || a.targetStudentIds?.includes(activeAccount.id);
        });
        const hasValidPractice = Object.values(practice).some(a => {
          if (a.deadline && new Date(a.deadline) < now) return false;
          if (a.completedByStudentIds?.includes(activeAccount.id)) return false;
          return a.wholeClass || a.targetStudentIds?.includes(activeAccount.id);
        });
        setHasActiveAssignments(hasValidAssessments || hasValidPractice);
      };
      checkAssignments();
    }
  }, [isLearner, activeAccount?.id, isOpen]);
  // Prevent body scroll when menu is open
  useBodyScrollLock(isOpen);
  const navItems = getLearnerNavItems();
  const handleNavigate = (view: string) => {
    onNavigate(view);
    onClose();
  };
  return <AnimatePresence data-id="element-1523">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 md:hidden" data-id="element-1524" />

          {/* Menu Panel */}
          <motion.div className="fixed inset-y-0 left-0 w-80 bg-white shadow-2xl z-50 flex flex-col" initial={{
        x: '-100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '-100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} data-id="element-1525">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200" data-id="element-1526">
              <div className="flex items-center gap-3" data-id="element-1527">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center shadow-md transform rotate-3" data-id="element-1528">
                  <span className="text-white font-black text-xl" data-id="element-1529">M</span>
                </div>
                <span className="font-black text-slate-800 text-xl tracking-tight" data-id="element-1530">
                  {APP_NAME}
                </span>
              </div>
              <button onClick={onClose} className="p-2 bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-800 rounded-full transition-colors" aria-label="Sluiten" data-id="element-1531">
                <X className="w-6 h-6 stroke-[2.5]" data-id="element-1532" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto py-4 px-4" data-id="element-1533">
              <div className="space-y-5" data-id="element-1534">
                {/* Profile Card */}
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-4 border border-purple-100" data-id="element-1535">
                  <div className="flex items-center gap-3" data-id="element-1536">
                    {/* Avatar with custom image support */}
                    {isLearner && avatarUrl ? <img src={avatarUrl} alt={displayName} className="w-12 h-12 rounded-full object-cover flex-shrink-0" data-id="element-1537" /> : <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0" data-id="element-1538">
                        {displayName.charAt(0).toUpperCase()}
                      </div>}
                    <div className="flex-1 min-w-0" data-id="element-1539">
                      <p className="font-semibold text-gray-900 truncate" data-id="element-1540">
                        {displayName}
                      </p>
                      {stats && <p className="text-sm text-gray-600" data-id="element-1541">
                          Level {stats.level} • {stats.xp} XP
                        </p>}
                    </div>
                  </div>
                </div>

                {/* Mijn Collectie Card */}
                {isLearner && <motion.button onClick={() => handleNavigate('collection')} className={`
                      w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left
                      ${currentView === 'collection' ? 'border-orange-400 bg-orange-50 ring-2 ring-orange-400 ring-offset-1' : 'border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 hover:border-orange-300'}
                    `} whileTap={{
              scale: 0.98
            }} data-id="element-1542">
                    <div className="relative flex-shrink-0" data-id="element-1543">
                      <motion.div animate={{
                  y: [0, -3, 0]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-1544">
                        <DjayAvatar size="sm" emotion="excited" variant={selectedVariant} data-id="element-1545" />
                      </motion.div>
                    </div>
                    <div className="flex-1 overflow-hidden" data-id="element-1546">
                      <span className="block font-black text-orange-900 text-base truncate" data-id="element-1547">
                        Mijn Collectie
                      </span>
                      <span className="block text-xs font-bold text-orange-600" data-id="element-1548">
                        {unlockedCount}/{TOTAL_VARIANTS} Varianten •{' '}
                        {unlockedAchievementsCount}/{ACHIEVEMENTS.length}{' '}
                        Achievements
                      </span>
                    </div>
                  </motion.button>}

                {/* Navigation Items */}
                <nav className="space-y-1.5" data-id="element-1549">
                  {navItems.map(item => {
                const isActive = currentView === item.id;
                const Icon = item.icon;
                return <motion.button key={item.id} onClick={() => handleNavigate(item.id)} className={`
                          w-full flex items-center gap-3 px-4 py-3 rounded-lg
                          transition-colors
                          ${isActive ? 'bg-gradient-to-r from-purple-500/20 to-blue-500/20 text-purple-600' : 'text-gray-700 hover:bg-gray-100'}
                        `} whileTap={{
                  scale: 0.98
                }} data-id="element-1550">
                        <div className="relative flex-shrink-0" data-id="element-1551">
                          <Icon className="w-6 h-6" data-id="element-1552" />
                          {item.id === 'assessment' && hasActiveAssignments && <span className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 border-2 border-white rounded-full" data-id="element-1553" />}
                        </div>
                        <span className="font-medium text-base" data-id="element-1554">
                          {item.label}
                        </span>
                      </motion.button>;
              })}
                </nav>
              </div>
            </div>

            {/* Footer */}
            {onLogout && <div className="border-t border-gray-200 p-4" data-id="element-1555">
                <button onClick={() => {
            onClose();
            onLogout();
          }} className="flex items-center justify-center gap-3 text-slate-500 hover:text-rose-600 font-bold w-full p-4 rounded-2xl hover:bg-rose-50 border-2 border-transparent hover:border-rose-200 transition-colors" data-id="element-1556">
                  <LogOut className="w-6 h-6 stroke-[2.5]" data-id="element-1557" />
                  <span className="text-lg" data-id="element-1558">Uitloggen</span>
                </button>
              </div>}
          </motion.div>
        </>}
    </AnimatePresence>;
}